import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Phone, Users, Clock, ChevronDown, Check, Loader2 } from 'lucide-react';
import { useT } from '../utils/translate';
import { fetchTasks, upsertTask } from '../store/tasksSlice';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

export const TASK_TYPES = [
  { value: 'call',    icon: Phone, cls: 'text-emerald-700' },
  { value: 'meeting', icon: Users, cls: 'text-amber-700' },
  { value: 'other',   icon: Clock, cls: 'text-ink-tertiary' },
];

const pad = (n) => String(n).padStart(2, '0');
const dateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const nextHour = () => { const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0); return `${pad(d.getHours())}:00`; };

// Быстрая постановка задачи прямо над полем ввода (как в amoCRM):
// «Задача [тип] на [дата] [время] для [ответственный]» + текст + «Поставить / Отменить».
export default function QuickTaskComposer({ users = [], dealId = null, contactId = null, defaultAssignee = '', onCreated, onCancel }) {
  const t = useT();
  const dispatch = useDispatch();
  const stages = useSelector(s => s.tasks.stages);
  const meId = useSelector(s => s.auth.user?._id || s.auth.user?.id);
  const [type, setType] = useState('call');
  const [menu, setMenu] = useState(false);
  const [date, setDate] = useState(dateStr(new Date()));
  const [time, setTime] = useState(nextHour());
  const [assignee, setAssignee] = useState(defaultAssignee || meId || '');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { if (!stages?.length) dispatch(fetchTasks()); }, [dispatch, stages]);
  useEffect(() => { if (!assignee && (defaultAssignee || meId)) setAssignee(defaultAssignee || meId); }, [defaultAssignee, meId, assignee]);
  useEffect(() => {
    if (!menu) return undefined;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menu]);

  const cur = TASK_TYPES.find(x => x.value === type) || TASK_TYPES[2];
  const CurIcon = cur.icon;
  const shift = (days) => { const d = new Date(); d.setDate(d.getDate() + days); setDate(dateStr(d)); };

  const submit = async () => {
    const first = stages?.[0];
    if (!first) { toast.error(t('tasks.error')); return; }
    const due = new Date(`${date}T${time || '09:00'}`);
    if (isNaN(due)) { toast.error(t('quickTask.badDate')); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/tasks`, {
        title: text.trim() || t(`quickTask.type.${type}`),
        description: '',
        stageId: String(first._id || first.name),
        assignedTo: assignee || null,
        dueDate: due.toISOString(),
        type,
        contact: contactId || null,
        deal: dealId || null,
      });
      dispatch(upsertTask(res.data.task));
      toast.success(t('quickTask.created'));
      onCreated?.(res.data.task);
    } catch (e) {
      toast.error(e.response?.data?.message || t('tasks.error'));
    } finally { setSaving(false); }
  };

  const chip = (label, days) => (
    <button type="button" onClick={() => shift(days)}
      className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${date === dateStr(new Date(Date.now() + days * 86400000)) ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-surface-200 text-ink-tertiary hover:border-primary-300'}`}>
      {label}
    </button>
  );

  return (
    <div className="bg-surface-50 rounded-xl border border-surface-200 p-3 space-y-2.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm">
        <div ref={menuRef} className="relative">
          <button type="button" onClick={() => setMenu(v => !v)}
            className="inline-flex items-center gap-1.5 text-primary-600 font-medium hover:underline">
            <CurIcon className={`w-4 h-4 ${cur.cls}`} />{t(`quickTask.type.${type}`)}<ChevronDown className="w-3.5 h-3.5" />
          </button>
          {menu && (
            <div className="absolute left-0 bottom-full mb-1 z-30 w-44 bg-white border border-surface-200 rounded-xl shadow-modal py-1">
              {TASK_TYPES.map(x => (
                <button key={x.value} type="button" onClick={() => { setType(x.value); setMenu(false); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-surface-50">
                  <x.icon className={`w-4 h-4 ${x.cls}`} />
                  <span className="flex-1">{t(`quickTask.type.${x.value}`)}</span>
                  {type === x.value && <Check className="w-3.5 h-3.5 text-primary-600" />}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="text-ink-tertiary">{t('quickTask.on')}</span>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="input !py-1 !px-2 text-sm w-auto" />
        <input type="time" value={time} onChange={e => setTime(e.target.value)} className="input !py-1 !px-2 text-sm w-auto" />
        <span className="text-ink-tertiary">{t('quickTask.for')}</span>
        <select value={assignee} onChange={e => setAssignee(e.target.value)} className="input !py-1 !px-2 text-sm w-auto max-w-[180px]">
          <option value="">{t('assignees.none')}</option>
          {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chip(t('quickTask.today'), 0)}{chip(t('quickTask.tomorrow'), 1)}{chip(t('quickTask.week'), 7)}
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} autoFocus
        onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); } }}
        placeholder={t('quickTask.placeholder')}
        className="w-full bg-white rounded-lg border border-surface-200 focus:border-primary-300 outline-none focus:ring-0 text-sm px-3 py-2 resize-none" />
      <div className="flex items-center gap-4">
        <button type="button" onClick={submit} disabled={saving}
          className="btn-primary btn-md inline-flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}{t('quickTask.place')}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="text-sm text-ink-secondary hover:text-ink">{t('quickTask.cancel')}</button>
        <span className="ml-auto text-xs text-ink-tertiary hidden sm:block">Ctrl+Enter</span>
      </div>
    </div>
  );
}
